import { useMutation } from '@tanstack/react-query';
import { splitEvery } from 'ramda';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { getErrorContent } from 'common/utils';
import { offeringApiDeleteOffers } from 'modules/shared-menu/api';
import { OfferingApiDeleteOffersPayload, OfferingApiError } from 'modules/shared-menu/types';

type ExecuteBatchOffersArgs = {
  payload: OfferingApiDeleteOffersPayload;
  idsToDelete: string[];
  onSuccess: (idsOfSuccessfullyDeletedOffers: Set<string>) => void;
  onError: (updateErrors: OfferingApiError[]) => void;
};

type MutationArgs = { payload: ExecuteBatchOffersArgs['payload']; idsToDelete: ExecuteBatchOffersArgs['idsToDelete'] };

const OFFER_CHUNK_SIZE = 100;

export const useBatchDeleteOffers = () => {
  const intl = useIntl();

  const { mutateAsync, isLoading } = useMutation(async ({ payload, idsToDelete }: MutationArgs) =>
    offeringApiDeleteOffers(payload, idsToDelete),
  );

  const executeBatchDeleteOffers = useCallback(
    async ({ payload, idsToDelete, onSuccess, onError }: ExecuteBatchOffersArgs) => {
      const idChunksArray = splitEvery(OFFER_CHUNK_SIZE, idsToDelete);
      const updateErrors: OfferingApiError[] = [];
      const idsOfSuccessfullyDeletedOffers: Set<string> = new Set();

      for (const idChunk of idChunksArray) {
        try {
          const response = await mutateAsync({ payload, idsToDelete: idChunk });
          const errorIdsSet = new Set((response?.errors ?? []).map(error => error.id));

          idChunk.forEach(id => {
            if (!errorIdsSet.has(id)) {
              idsOfSuccessfullyDeletedOffers.add(id);
            }
          });

          if (!!response?.errors && response.errors.length > 0) {
            updateErrors.push(...response.errors);
          }
        } catch (e) {
          const offerErrors: OfferingApiError[] = idChunk.map(id => ({
            id,
            code: '',
            message: getErrorContent(e) || intl.formatMessage({ id: 'errors.unknown-error-occurred' }),
          }));
          updateErrors.push(...offerErrors);
        }
      }

      if (idsOfSuccessfullyDeletedOffers.size > 0) {
        onSuccess(idsOfSuccessfullyDeletedOffers);
      }

      if (updateErrors.length > 0) {
        onError(updateErrors);
      }
    },
    [intl, mutateAsync],
  );

  return { executeBatchDeleteOffers, isLoading };
};
